/**
 * WhatsApp Deployment Settings Schema
 *
 * Zod schema for Deployment.deploymentSettings when type = WHATSAPP.
 * Single source of truth for WhatsApp deployment configuration.
 */

import { z } from "zod";

import { BaseSettingsSchema } from "../Base.schema";
import {
  ConcurrencyStrategy,
  DEFAULT_MULTI_MESSAGE_HANDLING,
  type MultiMessageHandling,
  MultiMessageHandlingSchema
} from "../MultiMessageHandling.schema";
import {
  ElevenLabsVoiceIdSchema,
  LegacyElevenLabsVoiceIdSchema,
  resolveElevenLabsVoiceId
} from "./ElevenLabsVoices.schema";
import { WhatsAppTemplateMessageSchema } from "./WhatsAppTemplateMessage.schema";

export type { MultiMessageHandling };
export { ConcurrencyStrategy, DEFAULT_MULTI_MESSAGE_HANDLING, MultiMessageHandlingSchema };

// ============================================================
// VOICE PROVIDER
// ============================================================

export const WHATSAPP_VOICE_PROVIDERS = ["openai", "elevenlabs"] as const;
export const WhatsAppVoiceProviderSchema = z.enum(WHATSAPP_VOICE_PROVIDERS);
export type WhatsAppVoiceProvider = z.infer<typeof WhatsAppVoiceProviderSchema>;

// ============================================================
// VOICE MODELS (per provider)
// ============================================================

/**
 * OpenAI TTS voice models for voice responses
 */
export const WHATSAPP_VOICE_MODELS = ["alloy", "echo", "fable", "onyx", "nova", "shimmer"] as const;
export const WhatsAppVoiceModelSchema = z.enum(WHATSAPP_VOICE_MODELS);
export type WhatsAppVoiceModel = z.infer<typeof WhatsAppVoiceModelSchema>;

/**
 * The voice a WhatsApp deployment SPEAKS for what it stores, per provider.
 *
 * The one read every surface makes — the reply path, the dispatch path and the
 * settings screen — so the voice that is heard and the voice that is labelled
 * cannot disagree. OpenAI keeps the stored name (the synthesis adapter degrades
 * an unknown one to "alloy"); ElevenLabs resolves through the catalog, so a
 * legacy id speaks its replacement and an absent one speaks the default.
 */
export const DEFAULT_OPENAI_WHATSAPP_VOICE: WhatsAppVoiceModel = "alloy";

export function resolveWhatsAppVoiceModel(
  provider: WhatsAppVoiceProvider,
  stored: string | undefined
): string {
  return provider === "elevenlabs"
    ? resolveElevenLabsVoiceId(stored)
    : (stored ?? DEFAULT_OPENAI_WHATSAPP_VOICE);
}

// ============================================================
// UPFRONT ACTION SCHEMA
// ============================================================

/**
 * Upfront action configuration
 * Actions to execute before processing a message
 */
export const UpfrontActionSchema = z.object({
  name: z.string(),
  input: z.unknown()
});

export type UpfrontAction = z.infer<typeof UpfrontActionSchema>;

// ============================================================
// MAIN SCHEMA (Complete WhatsApp Deployment Settings)
// ============================================================

/**
 * Complete WhatsApp Deployment Settings Schema
 *
 * This is the SINGLE SOURCE OF TRUTH for Deployment.deploymentSettings
 * when type = "WHATSAPP". Stored as JSON in Prisma, validated with this schema.
 *
 * NOTE: agentId is NOT in deploymentSettings! It's a Deployment column (FK to Agent).
 */
export const WhatsAppRedirectLinkSchema = z.object({
  enabled: z.boolean().default(false),
  /** Token expiry in hours (1-168, default 24) */
  tokenExpiryHours: z.number().min(1).max(168).default(24)
});

export type WhatsAppRedirectLink = z.infer<typeof WhatsAppRedirectLinkSchema>;

export const WhatsAppDeploymentSettingsSchema = z
  .object({
    // Upfront actions to execute before processing
    upfrontActions: z.array(UpfrontActionSchema).optional(),

    // WhatsApp template messages configuration
    whatsappTemplateMessages: z.array(WhatsAppTemplateMessageSchema).optional(),
    allowAgentToCreateAndSendTemplates: z.boolean().default(false),
    /**
     * A free-form card send that is the LAST tool call of its assistant
     * message ends the agent's turn (NEX-4407). Default ON: it removes the
     * forced follow-up completion that invites the model to narrate its own
     * actions to the customer ("I've asked about X. Waiting for the
     * customer's response."). The default assumes the card-last discipline
     * (tools first, card last, card self-sufficient); a deployment whose
     * prompt sends a filler card ALONE and continues in a later completion
     * must opt OUT with `false`, or that filler becomes the end of the turn
     * (the mid-turn stranding NEX-3144 fixed). A card followed by more calls
     * in the SAME assistant message is unaffected either way.
     */
    cardEndsTurn: z.boolean().default(true),

    // Voice response settings
    enableVoiceResponses: z.boolean().optional(),
    voiceProvider: WhatsAppVoiceProviderSchema.optional(),
    voiceModel: z
      .union([WhatsAppVoiceModelSchema, ElevenLabsVoiceIdSchema, LegacyElevenLabsVoiceIdSchema])
      .optional(),
    voiceInstructions: z.string().optional(),

    // Redirect link settings (NEX-1563: shadow metadata)
    redirectLink: WhatsAppRedirectLinkSchema.optional()
  })
  .merge(BaseSettingsSchema);

export type WhatsAppDeploymentSettings = z.infer<typeof WhatsAppDeploymentSettingsSchema>;

// ============================================================
// DEFAULTS (Single Source of Truth)
// ============================================================

/**
 * Default values for creating a new WhatsApp deployment.
 */
export const DEFAULT_WHATSAPP_SETTINGS: WhatsAppDeploymentSettings = {
  allowAgentToCreateAndSendTemplates: false,
  cardEndsTurn: true,
  enableVoiceResponses: false,
  whatsappTemplateMessages: [],
  upfrontActions: [],
  multiMessageHandling: DEFAULT_MULTI_MESSAGE_HANDLING
};

// ============================================================
// FACTORY FUNCTION (Type-Safe Creation)
// ============================================================

/**
 * Create complete WHATSAPP deployment settings with defaults.
 *
 * Accepts unknown (e.g. raw Prisma JsonValue) and merges with defaults.
 * Callers should follow up with WhatsAppDeploymentSettingsSchema.safeParse()
 * to validate the result.
 *
 * @param overrides - Raw settings from DB or partial overrides (optional)
 * @returns Settings merged with defaults — not yet Zod-validated
 */
export function createWhatsAppDeploymentSettings(overrides?: unknown): WhatsAppDeploymentSettings {
  if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) {
    return { ...DEFAULT_WHATSAPP_SETTINGS };
  }

  return { ...DEFAULT_WHATSAPP_SETTINGS, ...overrides } as WhatsAppDeploymentSettings;
}
